(() => {
  const register = (name, initializer) => {
    if (window.SafeWalkV51Product?.register) window.SafeWalkV51Product.register(name, initializer);
    else (window.SafeWalkV51ProductQueue ||= []).push([name, initializer]);
  };

  register('sticky', ({ root, listen, addCleanup }) => {
    const stickyActions = root.querySelector('[data-swv51-sticky-actions]');
    const mobileSticky = root.querySelector('[data-mobile-atc], .swv5-mobile-atc');
    const desktopSticky = root.querySelector('[data-desktop-atc]');
    const whatsapp = root.querySelector('.swv5-wa-float');
    if (!mobileSticky && !desktopSticky && !whatsapp) return;
    const mobileTrigger = Number(stickyActions?.dataset.stickyMobileTrigger || 520);
    const desktopTrigger = Number(stickyActions?.dataset.stickyDesktopTrigger || 360);
    const mobileViewportQuery = window.matchMedia('(max-width: 989px)');
    const desktopViewportQuery = window.matchMedia('(min-width: 990px)');
    const closingBand = document.querySelector('.fp-closing');
    const sizeSelection = root.querySelector('.swv5-option--size');
    const sizeFocus = sizeSelection?.querySelector('[data-size-choice-focus]') || sizeSelection;
    let atcs = Array.from(root.querySelectorAll('[data-atc]'));
    const atcVisibility = new Map();
    let atcObserver = null;
    let footerVisible = false;
    let updateFrame = 0;
    let highlightTimer = 0;

    const explicitSizeChosen = () => {
      const checkedSize = sizeSelection?.querySelector('[data-option-value]:checked');
      return root.dataset.sizeExplicitChoice === 'true'
        && Boolean(checkedSize?.value)
        && checkedSize.value === root.dataset.sizeConfirmedValue;
    };
    const focusSize = () => {
      if (!sizeSelection) return;
      sizeSelection.classList.add('is-size-choice-needed');
      sizeSelection.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start'
      });
      window.clearTimeout(highlightTimer);
      highlightTimer = window.setTimeout(() => sizeSelection.classList.remove('is-size-choice-needed'), 1200);
      window.setTimeout(() => sizeFocus?.focus?.({ preventScroll: true }), 450);
    };

    const setMode = (sticky) => {
      if (!sticky) return false;
      const needsSize = !explicitSizeChosen();
      const nextMode = needsSize ? 'size' : 'submit';
      const modeChanged = sticky.dataset.stickyActionMode !== nextMode;
      sticky.querySelectorAll('[data-sticky-size-scroll]').forEach((button) => {
        button.hidden = !needsSize;
      });
      sticky.querySelectorAll('[data-swv5-submit]').forEach((button) => {
        button.hidden = needsSize;
      });
      sticky.dataset.stickyActionMode = nextMode;
      return modeChanged;
    };

    const setVisibility = (sticky, visible) => {
      if (!sticky) return;
      const modeChanged = setMode(sticky);
      if (!modeChanged && sticky.classList.contains('is-visible') === visible) return;
      sticky.classList.toggle('is-visible', visible);
      sticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
      sticky.toggleAttribute('inert', !visible);
      sticky.querySelectorAll('button').forEach((button) => {
        if (visible && !button.hidden) button.removeAttribute('tabindex');
        else button.setAttribute('tabindex', '-1');
      });
    };

    const update = () => {
      const mobileViewport = mobileViewportQuery.matches;
      document.body.classList.toggle(
        'swv5-mobile-floats-ready',
        !mobileViewport || window.scrollY > mobileTrigger
      );
      if (!atcs.length) {
        setVisibility(desktopSticky, false);
        setVisibility(mobileSticky, false);
        document.body.classList.remove('swv5-mobile-atc-visible', 'swv5-primary-atc-visible');
        return;
      }
      const atcVisible = atcObserver
        ? atcs.some((button) => atcVisibility.get(button) === true)
        : atcs.some((button) => {
          if (!button.getClientRects().length) return false;
          const rect = button.getBoundingClientRect();
          return rect.top < window.innerHeight && rect.bottom > 0;
        });
      document.body.classList.toggle(
        'swv5-primary-atc-visible',
        mobileViewport && window.scrollY > mobileTrigger && atcVisible
      );
      if (desktopSticky) {
        let closingTakesOver = false;
        if (desktopViewportQuery.matches) {
          const closingRect = closingBand?.getBoundingClientRect();
          closingTakesOver = Boolean(closingRect && closingRect.top < window.innerHeight * .82);
        }
        const showDesktop = desktopViewportQuery.matches
          && window.scrollY > desktopTrigger
          && !atcVisible
          && !closingTakesOver
          && !footerVisible;
        setVisibility(desktopSticky, showDesktop);
      }
      const showMobile = Boolean(
        mobileSticky
        && mobileViewport
        && window.scrollY > mobileTrigger
        && !atcVisible
        && !footerVisible
      );
      setVisibility(mobileSticky, showMobile);
      document.body.classList.toggle('swv5-mobile-atc-visible', showMobile);
    };

    const scheduleUpdate = () => {
      if (updateFrame) return;
      updateFrame = window.requestAnimationFrame(() => {
        updateFrame = 0;
        update();
      });
    };

    const observeAtcs = () => {
      if (!atcObserver) return;
      atcObserver.disconnect();
      atcVisibility.clear();
      atcs.forEach((button) => {
        atcVisibility.set(button, false);
        atcObserver.observe(button);
      });
    };

    if ('IntersectionObserver' in window) {
      atcObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => atcVisibility.set(entry.target, entry.isIntersecting));
        scheduleUpdate();
      });
      observeAtcs();
      addCleanup(() => atcObserver.disconnect());
    }

    stickyActions?.querySelectorAll('[data-sticky-size-scroll]').forEach((button) => listen(button, 'click', focusSize));
    listen(root, 'swv5:size-choice-state', scheduleUpdate);
    listen(window, 'scroll', scheduleUpdate, { passive: true });
    listen(window, 'resize', scheduleUpdate);
    listen(mobileViewportQuery, 'change', scheduleUpdate);
    listen(desktopViewportQuery, 'change', scheduleUpdate);
    const config = root.querySelector('.swv5-config');
    if (config && 'MutationObserver' in window) {
      const contentObserver = new MutationObserver(() => {
        const nextAtcs = Array.from(root.querySelectorAll('[data-atc]'));
        const atcsChanged = nextAtcs.length !== atcs.length || nextAtcs.some((button, index) => button !== atcs[index]);
        if (atcsChanged) {
          atcs = nextAtcs;
          observeAtcs();
          scheduleUpdate();
        }
      });
      contentObserver.observe(config, { childList: true, subtree: true });
      addCleanup(() => contentObserver.disconnect());
    }
    const footer = document.querySelector('footer.footer, .fp-footer');
    if (footer && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(([entry]) => {
        footerVisible = entry.isIntersecting;
        scheduleUpdate();
      });
      observer.observe(footer);
      addCleanup(() => observer.disconnect());
    }
    if (stickyActions && !window.Shopify?.designMode) {
      const origin = document.createComment('swv51-sticky-actions-origin');
      stickyActions.before(origin);
      document.body.append(stickyActions);
      addCleanup(() => {
        if (origin.isConnected) origin.replaceWith(stickyActions);
        else stickyActions.remove();
      });
    }
    update();

    return () => {
      window.cancelAnimationFrame(updateFrame);
      window.clearTimeout(highlightTimer);
      sizeSelection?.classList.remove('is-size-choice-needed');
      setVisibility(desktopSticky, false);
      setVisibility(mobileSticky, false);
      document.body.classList.remove(
        'swv5-mobile-atc-visible',
        'swv5-primary-atc-visible',
        'swv5-mobile-floats-ready',
        'swv6-personalization-editing'
      );
    };
  });
})();
